import { Injectable, OnDestroy, inject, signal, TemplateRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, Subscription } from 'rxjs';
import { RoutesExtended, SidebarData, SidebarComponentItem, SidebarItem } from '../sidebar-types';

@Injectable()
export class SidebarService implements OnDestroy{
  private pageMap: Map<String, SidebarData> = new Map();
  private componentItems: Map<String, SidebarComponentItem> = new Map();
  readonly drawerWidth$ = new BehaviorSubject<number>(0);
  private ro?: ResizeObserver;
  public drawer: any; // Changed from MatDrawer to any

  opened$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  drawerReady$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private drawerSub = new Subscription();

  public pages = signal<SidebarItem[]>([]);

  constructor(public route: ActivatedRoute, public router: Router) {}

  setSidebarRoutes(mapRoutes: RoutesExtended) {
    mapRoutes
      .filter((page) => page.data)
      .sort((a, b) => a.data.sidebar.position - b.data.sidebar.position)
      .forEach((page) =>
        this.pageMap.set(
          page.path.includes('/:') ? page.path.split('/:')[0] : page.path,
          page.data.sidebar
        )
      );

    this.updatePages();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.deactivateAllPages();
        let url = event.urlAfterRedirects;
        let pageName = url.split('/')[2];
        if (this.pageMap.has(pageName)) {
          this.pageMap.get(pageName).active = true;
        }
        this.updatePages();
      });
  }

  // Add component-based sidebar items
  addComponentItem(item: SidebarComponentItem) {
    this.componentItems.set(item.id, item);
    this.updatePages();
  }

  // Remove component-based sidebar items
  removeComponentItem(id: string) {
    this.componentItems.delete(id);
    this.updatePages();
  }

  // Update component-based sidebar items
  updateComponentItem(id: string, updates: Partial<SidebarComponentItem>) {
    const item = this.componentItems.get(id);
    if (item) {
      this.componentItems.set(id, { ...item, ...updates });
      this.updatePages();
    }
  }

  // Set active state for component-based items
  setComponentItemActive(id: string, active: boolean) {
    const item = this.componentItems.get(id);
    if (item) {
      item.active = active;
      this.updatePages();
    }
  }

  // Check if a component item is active
  isComponentActive(id: string): boolean {
    const item = this.componentItems.get(id);
    return item ? item.active : false;
  }

  // Toggle component item active state
  toggleComponentItem(id: string) {
    const item = this.componentItems.get(id);

    if (item) {
      // Deactivate all other component items first
      this.componentItems.forEach((compItem, compId) => {
        if (compId !== id) {
          compItem.active = false;
        }
      });

      // Toggle the current item
      item.active = !item.active;
      this.updatePages();

      // Update sidebar state based on component activation
      this.updateSidebarState();
    }
  }

  // Update sidebar open/close state based on component items
  private updateSidebarState() {
    const hasActiveComponent = Array.from(this.componentItems.values()).some(item => item.active);

    if (hasActiveComponent) {
      this.open();
    } else {
      this.close();
    }
  }

  // Combine route-based and component-based items
  private updatePages() {
    const routeItems = Array.from(this.pageMap.values());
    const componentItems = Array.from(this.componentItems.values());

    // Combine and sort by position
    const allItems = [...routeItems, ...componentItems].sort((a, b) => a.position - b.position);

    this.pages.set(allItems);
  }

  setDrawer(drawer: any) {
    this.drawerSub.unsubscribe();
    this.drawer = drawer;

    // Initialize drawer state
    this.opened$.next(false);

    // Set up ResizeObserver for width tracking
    if (drawer && drawer.nativeElement) {
      this.ro = new ResizeObserver(() => this.pushWidth());
      this.ro.observe(drawer.nativeElement);

    } else {

    }
  }

  private pushWidth() {
    if (this.drawer?.nativeElement) {
      const el = this.drawer.nativeElement as HTMLElement;
      this.drawerWidth$.next(el.getBoundingClientRect().width);
    } else {
      this.drawerWidth$.next(0);
    }
  }

  close() {
    if (this.drawer?.nativeElement) {
      this.drawer.nativeElement.classList.remove('sidebar-open');
    }
    this.opened$.next(false);
  }

  open() {
    if (this.drawer?.nativeElement) {
      this.drawer.nativeElement.classList.add('sidebar-open');
    }
    this.opened$.next(true);
  }

  getPageMap() {
    return this.pageMap;
  }

  getComponentItems() {
    return this.componentItems;
  }

  // Get content template for a specific component item
  getContentTemplate(id: string): TemplateRef<any> | null {
    const item = this.componentItems.get(id);
    return item?.contentTemplate || null;
  }

  // Get sidebar icon template for a specific component item
  getSidebarIconTemplate(id: string): TemplateRef<any> | null {
    const item = this.componentItems.get(id);
    return item?.sidebarIconTemplate || null;
  }

  // Get header template for a specific component item
  getHeaderTemplate(id: string): TemplateRef<any> | null {
    const item = this.componentItems.get(id);
    return item?.headerTemplate || null;
  }

  private deactivateAllPages() {
    this.pageMap.forEach((page) => (page.active = false));
    this.componentItems.forEach((item) => (item.active = false));
    this.updateSidebarState();
  }

  ngOnDestroy() {
    this.drawerSub.unsubscribe();
    this.opened$.complete();
    this.drawerWidth$.complete();
    this.drawerReady$.complete();
    if (this.ro) {
      this.ro.disconnect();
    }
  }
}
